import { useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AdminFormPage } from "@components/CatalogIndex";
import ToastAlert from "@components/Alerts/ToastAlert/ToastAlert";
import { WebhookSubscriptionForm } from "../WebhookSubscriptionForm/WebhookSubscriptionForm";
import { useWebhookSubscriptionMutations } from "../api/useWebhookSubscriptionMutations";
import { emptyWebhookSubscriptionForm } from "../types";
import type { WebhookSubscriptionFormValues } from "../types";

const FORM_ID = "webhook-subscription-form";

export const New = (): JSX.Element => {
  const navigate = useNavigate();
  const { create } = useWebhookSubscriptionMutations();
  const [submitting, setSubmitting] = useState(false);
  // Remounting WebhookSubscriptionForm via key resets it to an empty form after
  // "Save and Add Another".
  const [formKey, setFormKey] = useState(0);
  const [toast, setToast] = useState<{
    variant: "success" | "danger";
    text: string;
  } | null>(null);
  // The native Save button submits the form directly (nextAction stays "edit");
  // "Save and Add Another" sets "new" then programmatically submits. handleSubmit
  // resets it to "edit" so a later plain Save navigates as expected.
  const nextAction = useRef<"edit" | "new">("edit");

  const handleSubmit = async (values: WebhookSubscriptionFormValues) => {
    setSubmitting(true);
    const addAnother = nextAction.current === "new";
    try {
      const result: any = await create(values);
      const newId = result?.data?.id;
      if (addAnother) {
        setFormKey((k) => k + 1);
        setToast({ variant: "success", text: "Webhook subscription saved" });
        setSubmitting(false);
      } else {
        navigate(newId ? `../${newId}/edit` : "..", {
          relative: "path",
          state: {
            toast: {
              variant: "success",
              text: "Webhook subscription saved",
            },
          },
        });
      }
    } catch (err) {
      console.error("Failed to create webhook subscription", err);
      setToast({ variant: "danger", text: "Could not save subscription." });
      setSubmitting(false);
    } finally {
      nextAction.current = "edit";
    }
  };

  const triggerAddAnother = () => {
    nextAction.current = "new";
    (
      document.getElementById(FORM_ID) as HTMLFormElement | null
    )?.requestSubmit();
  };

  return (
    <>
      <AdminFormPage
        title="Add a Webhook Subscription"
        formId={FORM_ID}
        onCancel={() => navigate("..")}
        onSaveAndAddAnother={triggerAddAnother}
        isSubmitting={submitting}
      >
        <WebhookSubscriptionForm
          key={formKey}
          id={FORM_ID}
          initialValues={emptyWebhookSubscriptionForm}
          isSubmitting={submitting}
          onSubmit={handleSubmit}
        />
      </AdminFormPage>
      {toast && (
        <ToastAlert
          variant={toast.variant}
          text={toast.text}
          onClose={() => setToast(null)}
        />
      )}
    </>
  );
};
